import { useCurrentUser } from '@api/application-configuration';
import { Form } from '@unistocks/components/form/form';
import { arrayTransform } from '@unistocks/components/form/validation-utils';
import { SelectProps } from '@unistocks/components/select/select';
import { mapSharedPortfolioToOption } from '@unistocks/features/shared-portfolios/shared-portfolio-utils';
import { t } from '@unistocks/localization';
import {
  AssetType,
  ListResultDtoOfLookupDtoOfGuid,
  ListResultDtoOfSectionLookupDto,
  PagedResultDtoOfSharedPortfolioDto
} from '@unistocks/proxy/axios-client';
import {
  usePortfoliosGetListQuery,
  usePortfoliosGetSectionLookupQuery
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import {
  useSharedPortfoliosGetListQuery
} from '@unistocks/proxy/axios-client/SharedPortfoliosQuery';
import { translateEnumOptions } from '@unistocks/shared/utils/enum-utils';
import { assetTypeOptions as assetTypeOptionsBase } from '@unistocks/shared/utils/enums/asset-type-options';
import { extractOptionId, extractOptionValue } from '@unistocks/shared/utils/list-utils';
import { SelectOption } from '@unistocks/types';
import React, { useCallback } from 'react';
import { useController } from 'react-hook-form';
import { GetProps } from 'tamagui';

type SelectOverrides = Omit<Partial<GetProps<typeof Form.Select>>, 'options' | 'form' | 'formId'>;
type PortfolioOption = SelectOption<{ id: string; creatorId: string }>;

export type PortfolioFilterProps = {
  sharedPortfolios?: boolean;
  portfolioSelectOverrides?: SelectOverrides;
  sectionSelectOverrides?: SelectOverrides;
  assetTypeSelectOverrides?: SelectOverrides & {
    /**
     * If provided, only the asset types in this list will be available for selection.
     */
    validAssetTypes?: AssetType[];
    /**
     * If provided, the asset types in this list will be disabled for selection.
     */
    invalidAssetTypes?: AssetType[];
  };
};

const mapPortfolios = (
  { items }: ListResultDtoOfLookupDtoOfGuid,
  creatorId: string
): PortfolioOption[] =>
  items.map(({ id, displayName }) => ({ key: displayName, value: { id, creatorId } }));

const mapSharedPortfolios = ({ items }: PagedResultDtoOfSharedPortfolioDto): PortfolioOption[] =>
  items.map(portfolio => ({
    ...mapSharedPortfolioToOption(portfolio),
    value: { id: portfolio.id!, creatorId: portfolio.creatorId! }
  }));

const mapSectionLookup = ({
  items
}: ListResultDtoOfSectionLookupDto): SelectOption<{ id: string; portfolioId: string }>[] =>
  items.map(
    ({ id, portfolioId, displayName }) =>
      ({
        key: displayName,
        value: {
          id,
          portfolioId
        }
      }) as SelectOption<{ id: string; portfolioId: string }>
  );

export const PortfolioFilter: React.FC<PortfolioFilterProps> = ({
  sharedPortfolios: enableSharedPortfolios = false,
  portfolioSelectOverrides = {},
  sectionSelectOverrides = {},
  assetTypeSelectOverrides = {}
}) => {
  const {
    field: { value: selectedPortfolios }
  } = useController({
    name: (portfolioSelectOverrides.name as 'portfolios') ?? 'portfolios'
  });

  const user = useCurrentUser();

  const { data: portfolios, isSuccess: isSuccessPortfolios } = usePortfoliosGetListQuery({
    enabled: !enableSharedPortfolios,
    select: useCallback(
      (res: ListResultDtoOfLookupDtoOfGuid) => mapPortfolios(res, user!.id!),
      [user]
    )
  });

  const { data: sharedPortfolios } = useSharedPortfoliosGetListQuery(0, 100, {
    enabled: enableSharedPortfolios,
    select: mapSharedPortfolios
  });

  const { data: sections } = usePortfoliosGetSectionLookupQuery(
    (selectedPortfolios ?? []).filter((id: string) =>
      portfolios?.some(portfolio => portfolio.value.id === id)
    ),
    {
      enabled: isSuccessPortfolios && !sectionSelectOverrides.hidden,
      select: mapSectionLookup
    }
  );

  const portfolioSelectOptions = (enableSharedPortfolios ? sharedPortfolios : portfolios) ?? [];

  const renderPortfolioGroupHeader = useCallback<
    NonNullable<SelectProps<PortfolioOption>['renderGroupHeader']>
  >(
    group => {
      if (!enableSharedPortfolios) return '';

      if (group === user?.id) return t('::MyPortfolios');

      const sharedPortfolio = sharedPortfolios?.find(({ value }) => value.creatorId === group);
      if (sharedPortfolio) return sharedPortfolio.subTitle ?? '';

      return '';
    },
    [enableSharedPortfolios, sharedPortfolios, user?.id]
  );

  const renderSectionGroupHeader = useCallback(
    (portfolioId: string) => portfolios?.find(({ value }) => value.id === portfolioId)?.key ?? '',
    [portfolios]
  );

  const assetTypeOptions = translateEnumOptions(assetTypeOptionsBase).filter(
    option =>
      (!assetTypeSelectOverrides.validAssetTypes?.length ||
        assetTypeSelectOverrides.validAssetTypes.includes(option.value)) &&
      (!assetTypeSelectOverrides.invalidAssetTypes?.length ||
        !assetTypeSelectOverrides.invalidAssetTypes.includes(option.value))
  );

  const multiplePortfolios = portfolioSelectOverrides.multiple ?? true;
  const portfoliosLabel = t(
    multiplePortfolios
      ? '::Dashboard:Settings:PortfolioFilter:Portfolios'
      : '::Dashboard:Settings:PortfolioFilter:Portfolio'
  );
  const portfoliosPlaceholder = t(
    multiplePortfolios
      ? '::Dashboard:Settings:PortfolioFilter:Portfolios:Placeholder'
      : '::Dashboard:Settings:PortfolioFilter:Portfolio:Placeholder'
  );

  return (
    <>
      <Form.Select
        name={'portfolios'}
        label={portfoliosLabel}
        placeholder={portfoliosPlaceholder}
        options={portfolioSelectOptions}
        multiple
        isRequired
        groupBy={enableSharedPortfolios ? 'creatorId' : undefined}
        valueKey="id"
        renderGroupHeader={renderPortfolioGroupHeader}
        {...portfolioSelectOverrides}
        keyExtractor={extractOptionId}
        valueTransform={arrayTransform}
      />

      <Form.Select
        name={'sections'}
        label={t('::Dashboard:Settings:PortfolioFilter:Categories')}
        placeholder={t('::All')}
        multiple
        {...sectionSelectOverrides}
        options={sections ?? []}
        groupBy={'portfolioId'}
        valueKey="id"
        renderGroupHeader={renderSectionGroupHeader}
        keyExtractor={extractOptionId}
        valueTransform={arrayTransform}
      />

      <Form.Select
        name={'assetTypes'}
        label={t('::Dashboard:Settings:PortfolioFilter:AssetTypes')}
        placeholder={t('::All')}
        multiple
        options={assetTypeOptions}
        {...assetTypeSelectOverrides}
        keyExtractor={option => String(extractOptionValue(option))}
        valueTransform={arrayTransform}
      />
    </>
  );
};
